import { Lead, PipelineType, LeadStatus } from '../types';

export interface PipelineStateResult {
  status: LeadStatus | string;
  pipeline: PipelineType;
  isArchived?: boolean;
}

/**
 * Calculates pipeline stage and status based on milestone dates and off-ramp disqualifier checkboxes.
 * Governing Rules of Movement:
 * 1. Date Fields drive forward momentum.
 * 2. Disqualifier Checkboxes trigger removal off-ramps (Unqualified / Lose).
 */
export function calculatePipelineState(lead: Partial<Lead>): PipelineStateResult {
  // 1. Off-Ramp Disqualifiers
  if (lead.is30DayUnresponsive) {
    return {
      status: '30-Day Unresponsive',
      pipeline: lead.pipeline || 'Opportunity',
      isArchived: true,
    };
  }

  if (lead.isTransferred) {
    return {
      status: 'Transferred',
      pipeline: 'Unqualified',
    };
  }

  if (lead.isDeclined) {
    return {
      status: 'Declined',
      pipeline: 'Unqualified',
    };
  }

  if (lead.isNotAwarded) {
    return {
      status: 'Not Awarded',
      pipeline: 'Lose',
    };
  }

  // 2. Date-Driven Forward Momentum (checked in reverse chronological order)
  if (lead.finalFollowUpDate && lead.finalFollowUpDate.trim() !== '') {
    return {
      status: 'Complete',
      pipeline: 'Won',
    };
  }

  if (lead.firstShipmentDate && lead.firstShipmentDate.trim() !== '') {
    return {
      status: 'Finalizing',
      pipeline: 'Won',
    };
  }

  if (lead.acceptedDate && lead.acceptedDate.trim() !== '') {
    return {
      status: 'Approved',
      pipeline: 'Won',
    };
  }

  if (lead.proposalDate && lead.proposalDate.trim() !== '') {
    return {
      status: 'Quoted',
      pipeline: 'Opportunity',
    };
  }

  if (lead.inquiryDate && lead.inquiryDate.trim() !== '') {
    return {
      status: 'Lead',
      pipeline: 'Opportunity',
    };
  }

  // Fallback to existing status & pipeline or default to Lead / Opportunity
  const fallbackStatus =
    lead.status === 'Customer Setup' || lead.status === 'Awarded' || lead.status === 'Accepted'
      ? 'Approved'
      : lead.status === 'Onboarding'
      ? 'Finalizing'
      : lead.status || 'Lead';
  const fallbackPipeline = (lead.pipeline as string) === 'Onboarding' || (lead.pipeline as string) === 'Finalizing' ? 'Won' : (lead.pipeline || 'Opportunity');
  return {
    status: fallbackStatus,
    pipeline: fallbackPipeline,
  };
}

/**
 * Checks if a completed lead has exceeded the 30-day retention window post First Shipment Date.
 */
export function isExceeded30DayRetention(firstShipmentDate?: string): boolean {
  if (!firstShipmentDate) return false;

  const shipmentDate = new Date(firstShipmentDate);
  if (isNaN(shipmentDate.getTime())) return false;

  const now = new Date();
  const diffMs = now.getTime() - shipmentDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays >= 30;
}
